import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronDown, Mail, SearchX } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  PageHeader,
  SearchInput,
  SegmentedControl,
  buttonVariants,
  cn,
} from '@solid/ui';
import { useIsAgencyPerson } from '@/hooks/useIsAgencyPerson';
import { useAuthStore, getIsAgencyAdmin, getIsAgencyMember, getCanCreateProjects } from '@/stores/auth.store';

/**
 * Help — answers about Solid Hub as it works today.
 *
 * Every answer was checked against the app (2026-10-01). When a feature
 * changes, change its answer here: quote UI labels exactly as they appear on
 * screen, and say who can do it. Items for agency staff or admins only are
 * tagged with an `audience` and hidden from everyone else, so a client never
 * reads about controls they don't have.
 *
 * The topic and search live in the URL (`?topic=&q=`), and each answer has an
 * anchor (`#<id>`) that opens it — so a support reply can link straight to one.
 */

/**
 * Who an answer is for. Omitted = everyone.
 * - staff: Solid Digital staff.
 * - agency: agency people: Solid staff and partner-agency staff (useIsAgencyPerson).
 * - admin: Solid Digital admins (platform settings).
 * - projectAdmin: anyone who runs projects — Solid admins, and a partner
 *   agency's admins for their own agency (see docs/partner-agencies.md).
 */
type Audience = 'staff' | 'agency' | 'admin' | 'projectAdmin';

const AUDIENCE_LABEL: Record<Audience, string> = {
  staff: 'Agency staff',
  agency: 'Agency staff',
  admin: 'Agency admins',
  projectAdmin: 'Agency admins',
};

interface Faq {
  /** Stable anchor: /help#<id>. Don't rename an id once it ships. */
  id: string;
  question: string;
  answer: string;
  audience?: Audience;
  /** Where to go next. Only rendered when the viewer can open it. */
  link?: { to: string; label: string; audience?: Audience };
}

interface Topic {
  id: string;
  label: string;
  faqs: Faq[];
}

const TOPICS: Topic[] = [
  {
    id: 'getting-around',
    label: 'Getting around',
    faqs: [
      {
        id: 'search',
        question: 'How do I find something quickly?',
        answer:
          'Use Search in the top bar. It covers projects, discussions, tasks, comments, events and files, and you can narrow it to one organization or project. Each project also has its own search.',
      },
      {
        id: 'pin-projects',
        question: 'How do I keep a project close at hand?',
        answer:
          'Click the star on a project in the Projects list, or “Add to favorites” in the project’s header. The “Favorites” star in the Solid app bar lists them, grouped by organization.',
        link: { to: '/projects', label: 'Go to Projects' },
        audience: 'agency',
      },
      {
        id: 'one-project',
        question: 'Why did Solid Hub open a project instead of the dashboard?',
        answer:
          'Solid Hub opens the project you had open last. Switch organization or project with the menus at the top of the sidebar.',
      },
      {
        id: 'app-bar',
        question: 'Where are my other Solid apps and my settings?',
        answer:
          'The Solid app bar runs along the edge of every Solid app. Use it to switch apps, send messages and sign out. Support and Settings are at the bottom of the sidebar.',
      },
      {
        id: 'time-tracking',
        question: 'Where do I track my time?',
        answer:
          'Time is tracked in Solid Financials, not in the Hub. Use the timer in the Solid app bar — it works from any Solid app.',
        audience: 'staff',
      },
      {
        id: 'send-message',
        question: 'How do I send a direct message?',
        answer:
          'Use Messages in the Solid app bar — it works from any Solid app. You can message anyone you share a project with.',
      },
    ],
  },
  {
    id: 'projects',
    label: 'Projects',
    faqs: [
      {
        id: 'create-project',
        question: 'How do I create a project?',
        answer:
          'On Projects, click “New Project”. Choose the organization — your agency for internal work, or a client — name the project and, if you like, add a description. Agency owners can also make it private, so only its members can see it.',
        audience: 'projectAdmin',
        link: { to: '/projects', label: 'Go to Projects', audience: 'projectAdmin' },
      },
      {
        id: 'project-members',
        question: 'Who’s on a project, and how are people added?',
        answer:
          'Click the “…” button next to the member avatars at the top of a project to see who’s on it. Agency admins add and remove people there, from the agency and from the client organization.',
      },
      {
        id: 'archive-project',
        question: 'How do I archive a project?',
        answer:
          'In the Projects list, open the “⋮” menu on the project and choose “Archive” — or use “Archive Project” in the project’s settings. Archived projects move to the “Archived” tab, and you can unarchive them at any time.',
        audience: 'projectAdmin',
      },
      {
        id: 'project-settings',
        question: 'What’s in a project’s settings?',
        answer:
          'The gear icon at the top of a project opens its settings: name, description and client organization, who gets emailed by default, discussion types, whether clients can create tasks, Slack (when it’s connected), and archive or delete.',
        audience: 'projectAdmin',
      },
    ],
  },
  {
    id: 'discussions',
    label: 'Discussions',
    faqs: [
      {
        id: 'start-discussion',
        question: 'How do I start a discussion?',
        answer:
          'In a project, click “New Discussion”, or “+ Add a discussion” on the Overview. Write your message, attach files if you need to, and check who should be emailed under “Notify” before you click “Post Discussion”. Some projects call discussions something else, such as “Requests”.',
      },
      {
        id: 'mentions',
        question: 'How do I @mention someone?',
        answer:
          'Type @ in a discussion, a reply or a task comment and pick a name — or “Everyone” to reach the whole project. They get an alert in their activity and an email, unless they’ve turned mention emails off. @mentions don’t work in direct messages.',
      },
      {
        id: 'who-is-emailed',
        question: 'Who gets emailed when I post?',
        answer:
          'Only the people checked under “Notify”. The list starts from the project’s default recipients. Someone marked “(off)” has turned off discussion emails and can’t be selected.',
      },
      {
        id: 'leave-thread',
        question: 'How do I stop getting emails from one discussion?',
        answer:
          'Open the discussion and click “Leave thread” under the reply box, next to the list of who gets emailed — “Rejoin” puts you back. You can also use the stop-notifications link at the bottom of any email from that discussion, then confirm on the page it opens.',
      },
      {
        id: 'reply-by-email',
        question: 'Can I reply from my email?',
        answer:
          'Yes. Reply to the email for a discussion or a task comment, and your reply is posted in Solid Hub.',
      },
      {
        id: 'edit-delete-messages',
        question: 'Can I edit or delete what I posted?',
        answer:
          'Yes — use the “⋮” menu on your post or reply. You can only edit your own messages. Agency staff can also delete a discussion to moderate it.',
      },
      {
        id: 'export-pdf',
        question: 'Can I save a discussion as a PDF?',
        answer: 'Yes. Open the discussion and choose “Export as PDF”.',
      },
      {
        id: 'discussion-statuses',
        question: 'What do discussion statuses mean?',
        answer:
          'Agency staff can give each discussion a status: Open, In Progress, Waiting on Solid, Waiting on Client, On Hold, Closed or Archived. Clients don’t see statuses. Your dashboard lists the discussions waiting on Solid.',
        audience: 'staff',
      },
      {
        id: 'schedule-discussion',
        question: 'Can I schedule a discussion for later?',
        answer:
          'Yes. While writing it, turn on “Schedule for later” and pick a time under “Post at”. Scheduled discussions wait in their own list, where you can reschedule them or post them right away.',
        audience: 'staff',
      },
      {
        id: 'seen-by',
        question: 'Can I see who has read a discussion?',
        answer: 'Yes. Agency admins see “Seen by” on each discussion.',
        audience: 'projectAdmin',
      },
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    faqs: [
      {
        id: 'add-task',
        question: 'How do I add a task?',
        answer:
          'Use “+ Add a task” on a project’s Overview or in the sidebar, or “Add Task” inside a list. Agency staff set the status, priority, assignees, and start and due dates in the task’s “Task settings” panel. Clients can add tasks when the project allows it.',
      },
      {
        id: 'task-lists',
        question: 'How are tasks organized?',
        answer:
          'Tasks live in lists, and each list shows how many are left and how many are done. Agency staff can make a list visible to everyone, to Solid Digital only, or to the client only. Clients can make a list just for their own team.',
      },
      {
        id: 'task-statuses',
        question: 'What do task statuses mean?',
        answer:
          'Tasks move through one shared set of statuses — by default Open, Blocked, In Progress, Internal Review, Client Review and Closed. Agency admins can change the set in Settings.',
        link: { to: '/settings/statuses', label: 'Edit statuses', audience: 'admin' },
      },
      {
        id: 'task-board',
        question: 'Can I see tasks as a board?',
        answer:
          'Yes. Open a list and switch to “Board view” to see its tasks in columns by status. On a project’s Tasks tab, “Compact view” summarises every list at once.',
      },
      {
        id: 'complete-task',
        question: 'How do I complete a task?',
        answer:
          'Tick its checkbox. Clients can complete the tasks assigned to them; agency staff can complete any task.',
      },
      {
        id: 'my-tasks',
        question: 'Where can I see all my tasks?',
        answer:
          'My tasks, in the Agency section of the sidebar, gathers tasks from all your projects: “Assigned to Me”, “All Tasks”, “Overdue” and “Completed”, plus a board view grouped by project.',
        link: { to: '/tasks', label: 'Go to My tasks' },
        audience: 'agency',
      },
      {
        id: 'bulk-edit-tasks',
        question: 'Can I change several tasks at once?',
        answer:
          'Yes. In a list, click “Select”, choose the tasks, then set their status, assignee, due date or priority, move them to another list, or delete them. Shift-click selects a range. Tasks you aren’t allowed to change are skipped.',
      },
      {
        id: 'copy-convert-task',
        question: 'Can I copy a task or turn it into a discussion?',
        answer:
          'Open the task and choose “Duplicate task” to copy it. Agency staff and project admins can also “Convert to discussion”, either moving the task or copying it.',
      },
      {
        id: 'due-reminders',
        question: 'Will I be reminded about due dates?',
        answer:
          'Yes, while “Task due reminders” is on in your notification settings: a daily email at 8 AM Eastern lists what’s due. Due dates are calendar days, so everyone sees the same date whatever their time zone.',
        link: { to: '/settings/notifications', label: 'Notification settings' },
      },
    ],
  },
  {
    id: 'files',
    label: 'Files',
    faqs: [
      {
        id: 'upload-files',
        question: 'How do I add files?',
        answer:
          'On a project’s Files tab, drag files anywhere onto the page or click “Upload Files”. You can also attach files to discussions, replies, tasks and comments.',
      },
      {
        id: 'file-folders',
        question: 'How are files organized?',
        answer:
          'Files you upload go in “All Files” and any folders you create. Files attached to discussions and tasks are collected in the “Discussions” and “Tasks” folders. Agency staff can move files between folders.',
      },
      {
        id: 'preview-files',
        question: 'Can I preview files?',
        answer:
          'Click a file to preview it. Images, video, audio, PDFs, text and Office documents open in the browser. Use the arrow keys to step through files, or download one.',
      },
      {
        id: 'bulk-files',
        question: 'How do I download or delete several files?',
        answer:
          'Select the files, then choose “Download as Zip” or “Move to Trash”. Trash keeps items for 30 days.',
      },
    ],
  },
  {
    id: 'schedule',
    label: 'Schedule',
    faqs: [
      {
        id: 'project-schedule',
        question: 'Where’s the project calendar?',
        answer:
          'A project’s Schedule tab shows its events and task due dates as a calendar or a list. Anyone on the project can click “Add Event” — all day, or spanning several days — and edit the events they added.',
      },
    ],
  },
  {
    id: 'forms',
    label: 'Forms',
    faqs: [
      {
        id: 'forms',
        question: 'What are forms?',
        answer:
          'Forms are questionnaires from the Solid Digital team. Any waiting for you appear on your dashboard under “Forms to complete” and on the Forms page.',
        link: { to: '/forms', label: 'Go to Forms' },
      },
      {
        id: 'manage-forms',
        question: 'How do I create and send a form?',
        answer:
          'On the Forms page, choose “Manage forms”, then “New form”. Build it, then assign it to the people who should fill it in. A project’s Forms tab shows the forms that belong to it.',
        audience: 'staff',
      },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    faqs: [
      {
        id: 'activity',
        question: 'Where do I see what’s new?',
        answer:
          'Each project’s Overview has an Activity card: the mentions, replies, task assignments and forms meant for you there. Unread items have a red dot, and opening one marks it read. Agency staff also have Activity in the sidebar, across every project.',
        link: { to: '/activity', label: 'Go to Activity', audience: 'agency' },
      },
      {
        id: 'email-settings',
        question: 'How do I choose which emails I get?',
        answer:
          'Your notification settings have a switch for each kind of email: Discussions, Mentions, Task assignments, Task due reminders, Forms and Announcements. They apply to all your projects.',
        link: { to: '/settings/notifications', label: 'Notification settings' },
      },
      {
        id: 'missing-email',
        question: 'Why didn’t I get an email?',
        answer:
          'First check that kind of email is switched on in your notification settings. For discussions, you’re only emailed when the author checks you under “Notify”, and not after you’ve left that thread.',
        link: { to: '/settings/notifications', label: 'Notification settings' },
      },
      {
        id: 'announcements',
        question: 'What are the banners at the top of a project?',
        answer:
          'Announcements from the Solid Digital team, for one project or for all of them. Agency admins post them from the Announcements page, with an optional email.',
        link: { to: '/announcements', label: 'Go to Announcements', audience: 'admin' },
      },
    ],
  },
  {
    id: 'account',
    label: 'Your account',
    faqs: [
      {
        id: 'profile',
        question: 'How do I change my photo, name or email?',
        answer:
          'Your profile settings let you upload or remove your photo. Your name, email and password belong to your Solid account — click “Manage Profile” there to change them.',
        link: { to: '/settings/profile', label: 'Profile settings' },
      },
      {
        id: 'support-hours',
        question: 'What is “Support hours”?',
        answer:
          'Projects with a support plan show a “Support hours” card on their Overview with the time used and remaining. Owners and admins of the client organization can buy more with “Get more hours”.',
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    faqs: [
      {
        id: 'admin-powers',
        question: 'What can agency admins do?',
        answer:
          'Agency owners and admins create and manage projects (members, settings, archive, delete), post announcements, and run Team, Statuses, Integrations, API Keys and Data in Settings. Any agency staff member can set discussion statuses, archive discussions and complete anyone’s task. Making a project private takes an owner.',
        audience: 'admin',
      },
      {
        id: 'add-team-member',
        question: 'How do I add someone to the team?',
        answer:
          'The Team settings list your team. “Add Members” opens the Solid accounts portal to send invitations, and the gear on a member changes their role.',
        audience: 'admin',
        link: { to: '/settings/team', label: 'Team settings', audience: 'admin' },
      },
      {
        id: 'export-projects',
        question: 'How do I export projects?',
        answer:
          'In Data settings, under “Export Projects”, pick the projects and download a ZIP with their discussions, tasks, comments, events and the files themselves.',
        audience: 'admin',
        link: { to: '/settings/data', label: 'Data settings', audience: 'admin' },
      },
      {
        id: 'import-projects',
        question: 'How do I import projects?',
        answer:
          'In Data settings, under “Import Projects”, upload an export ZIP, review the preview and choose the organization to import into. People are matched by email.',
        audience: 'admin',
        link: { to: '/settings/data', label: 'Data settings', audience: 'admin' },
      },
      {
        id: 'integrations',
        question: 'What can Solid Hub connect to?',
        answer:
          'Integrations settings connect Slack (alerts in your channels), ClickUp (tickets from those alerts) and Solid Financials. Basecamp import is there for bringing over older projects.',
        audience: 'admin',
        link: { to: '/settings/integrations', label: 'Integrations settings', audience: 'admin' },
      },
      {
        id: 'api-keys',
        question: 'How do other tools connect to Solid Hub?',
        answer:
          'API Keys settings issue a key for each tool, limited to the access you choose. A key is shown once, when it’s created; revoke it there when the tool no longer needs it.',
        audience: 'admin',
        link: { to: '/settings/api-keys', label: 'API Keys settings', audience: 'admin' },
      },
    ],
  },
];

const ALL = 'all';

/** Every word of the query, in any order, somewhere in the question, answer or topic. */
function matches(faq: Faq, topic: Topic, query: string): boolean {
  const haystack = `${faq.question} ${faq.answer} ${topic.label}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

export function Help() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = getIsAgencyAdmin(user);
  const isStaff = getIsAgencyMember(user);
  const runsProjects = getCanCreateProjects(user);
  const isAgencyPerson = useIsAgencyPerson();
  const canSee = (audience?: Audience) =>
    !audience ||
    (audience === 'staff'
      ? isStaff
      : audience === 'agency'
        ? isAgencyPerson
        : audience === 'projectAdmin'
          ? runsProjects
          : isAdmin);

  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const location = useLocation();

  // Only the answers this viewer may read, and topics that still have some.
  const topics = useMemo(
    () =>
      TOPICS.map((t) => ({ ...t, faqs: t.faqs.filter((f) => canSee(f.audience)) })).filter(
        (t) => t.faqs.length > 0,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, isStaff, runsProjects],
  );

  const requestedTopic = params.get('topic') ?? ALL;
  const topicId = topics.some((t) => t.id === requestedTopic) ? requestedTopic : ALL;

  const matching = useMemo(
    () =>
      topics.map((t) => ({ ...t, faqs: query ? t.faqs.filter((f) => matches(f, t, query)) : t.faqs })),
    [topics, query],
  );
  const shown = matching.filter((t) => t.faqs.length > 0 && (topicId === ALL || t.id === topicId));
  const total = matching.reduce((n, t) => n + t.faqs.length, 0);

  const setParam = (key: 'q' | 'topic', value: string) => {
    const next = new URLSearchParams(params);
    if (!value || (key === 'topic' && value === ALL)) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  // An answer named in the URL hash opens and scrolls into view.
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id || !topics.some((t) => t.faqs.some((f) => f.id === id))) return;
    setOpen((prev) => new Set(prev).add(id));
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
  }, [location.hash, topics]);

  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // The address bar now links to this answer (no new history entry).
        window.history.replaceState(window.history.state, '', `${location.pathname}${location.search}#${id}`);
      }
      return next;
    });
  };

  const clearFilters = () => setParams(new URLSearchParams(), { replace: true });

  return (
    <div>
      <PageHeader title="Support" subtitle="Answers about using Solid Hub" />

      <div className="mb-6 flex flex-col gap-3">
        <SearchInput
          searches="help"
          value={query}
          onChange={(value) => setParam('q', value)}
          size="md"
          className="w-full sm:max-w-md"
        />
        <SegmentedControl
          ariaLabel="Help topics"
          size="md"
          scrollable
          value={topicId}
          onChange={(value) => setParam('topic', value)}
          options={[
            { value: ALL, label: 'All', count: total },
            ...matching.map((t) => ({ value: t.id, label: t.label, count: t.faqs.length })),
          ]}
        />
      </div>

      {shown.length === 0 ? (
        <Card padding="md">
          <EmptyState
            icon={<SearchX className="h-6 w-6" aria-hidden />}
            title={query ? `No answers match “${query}”` : 'No answers here yet'}
            description="Try different words, look in all topics, or email us below."
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear search
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-8">
          {shown.map((topic) => (
            <section key={topic.id} aria-labelledby={`topic-${topic.id}`}>
              <h2 id={`topic-${topic.id}`} className="mb-3 text-heading-4 text-secondary">
                {topic.label}
              </h2>
              <Card padding="none" className="divide-y divide-grey-100">
                {topic.faqs.map((faq) => (
                  <FaqRow
                    key={faq.id}
                    faq={faq}
                    open={open.has(faq.id)}
                    onToggle={() => toggle(faq.id)}
                    canSee={canSee}
                    showAudience={isStaff || runsProjects}
                  />
                ))}
              </Card>
            </section>
          ))}
        </div>
      )}

      <Card padding="md" className="mt-10 gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-heading-4 text-secondary">Still stuck?</h2>
          <p className="text-body-sm text-grey-600">Email the Solid Digital team and we’ll help.</p>
        </div>
        <a href="mailto:support@soliddigital.com" className={cn(buttonVariants({ variant: 'outline' }), 'shrink-0')}>
          <Mail className="h-4 w-4" aria-hidden />
          Email support
        </a>
      </Card>
    </div>
  );
}

function FaqRow({
  faq,
  open,
  onToggle,
  canSee,
  showAudience,
}: {
  faq: Faq;
  open: boolean;
  onToggle: () => void;
  canSee: (audience?: Audience) => boolean;
  showAudience: boolean;
}) {
  const panelId = `${faq.id}-answer`;
  return (
    <div id={faq.id} className="scroll-mt-24">
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40"
        >
          <ChevronDown
            aria-hidden
            className={cn('mt-0.5 h-4 w-4 shrink-0 text-grey-400 transition-transform', open && 'rotate-180')}
          />
          <span className="flex-1 font-semibold text-secondary">{faq.question}</span>
          {showAudience && faq.audience && (
            <span className="shrink-0 pt-0.5 text-xs text-grey-500">{AUDIENCE_LABEL[faq.audience]}</span>
          )}
        </button>
      </h3>
      {open && (
        <div id={panelId} className="px-5 pb-5 pl-12">
          <p className="max-w-prose text-body-sm leading-relaxed text-grey-600">{faq.answer}</p>
          {faq.link && canSee(faq.link.audience) && (
            <Link to={faq.link.to} className="mt-3 inline-block text-body-sm font-semibold text-accent hover:underline">
              {faq.link.label}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
